import { Component, HostListener, Input, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { Subscription } from 'rxjs'
import { finalize, switchMap } from 'rxjs/operators'
import { Application } from 'src/app/account/services/application.service'
import { DrsAgentNotFoundIssue } from 'src/app/account/services/issue'
import { SyncPair } from 'src/app/account/services/sync-pair.service'
import { ApiService } from 'src/app/services/api.service'
import { ResourceConfigDto, SyncPairIssueScopeDto } from 'src/app/services/dtos'
import { UiService } from 'src/app/services/ui.service'
import { ModalDialogComponent } from 'src/app/shared/modal-dialog/modal-dialog.component'
import { handleArpioError } from 'src/utils/handle-error'
import { subscriptionActive } from 'src/utils/rxjs-utils'
import { ViewableIssue } from '../../issues-view/issues-view.component'

@Component({
  selector: 'drs-agent-not-found-issue',
  templateUrl: './drs-agent-not-found-issue.component.html',
  styleUrls: ['./drs-agent-not-found-issue.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DrsAgentNotFoundIssueComponent implements OnInit {
  @Input() issue: ViewableIssue = null
  @Input('sync-pair') syncPair: SyncPair = null
  @Input('application') application: Application = null
  @ViewChild('ConfirmEbsSnapshotReplicationDialog', { static: true })
  confirmEbsSnapshotReplicationDialog: ModalDialogComponent

  private savingSubscription: Subscription = null

  constructor(
    public uiService: UiService,
    public apiService: ApiService
  ) {}

  ngOnInit(): void {}

  get region() {
    return (this.syncPair && this.syncPair.targetRegion) || this.application.targetRegion
  }

  get awsAccountId() {
    return (this.syncPair && this.syncPair.targetAwsAccountId) || this.application.targetAwsAccountId
  }

  // Esc dismisses the confirmation dialog. Document-level so the key fires
  // regardless of where focus sits inside the modal. Short-circuits during
  // an in-flight save so an accidental Esc can't tear down the dialog
  // mid-request.
  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKeydown(event: KeyboardEvent): void {
    if (!this.confirmEbsSnapshotReplicationDialog?.isOpen) return
    if (this.saving) return
    event.preventDefault()
    this.dismissEbsSnapshotReplicationDialog()
  }

  showConfirmEbsSnapshotReplicationDialog() {
    this.confirmEbsSnapshotReplicationDialog.open()
  }

  confirmEbsSnapshotReplication() {
    let config = {
      resourceConfigType: 'ec2Instance',
      realTimeRpoEnabled: false,
    } as ResourceConfigDto

    let saver$ = null
    if (this.syncPair) {
      saver$ = this.syncPair.setSyncPairResourceConfig$(this.issue.arn, config)
    } else {
      saver$ = this.application.setSyncPairResourceConfig$(this.issue.arn, config)
    }
    this.savingSubscription = saver$
      .pipe(
        switchMap((_config) => this.issue.deleter$()),
        finalize(() => (this.savingSubscription = null))
      )
      .subscribe(
        (_response) => {
          this.dismissEbsSnapshotReplicationDialog()
        },
        handleArpioError(window.location.href, this.apiService, this.uiService)
      )
  }

  get saving() {
    return subscriptionActive(this.savingSubscription)
  }

  dismissEbsSnapshotReplicationDialog() {
    this.confirmEbsSnapshotReplicationDialog.close()
  }
}
